"use client";

import { useCallback, useMemo, useState, type FormEvent } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Mail, Lock } from "lucide-react";
import { SiLine } from "react-icons/si";
import { FcGoogle } from "react-icons/fc";
import { FaMicrosoft } from "react-icons/fa";
import Image from "next/image";
import Link from "next/link";

import { fetchCurrentUserClient, isStaff as isStaffUser } from "@/lib/authkit";

type Provider = "line" | "google" | "microsoft";

function isObjectRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

function getStringProp(o: unknown, key: string): string | undefined {
  return isObjectRecord(o) && typeof o[key] === "string" ? (o[key] as string) : undefined;
}

const envFlagOn = (v: string | undefined | null): boolean => v === "1";

export default function LoginForm({ signupHref = "/signup" }: { signupHref?: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const [socialPending, setSocialPending] = useState<null | Provider>(null);

  const router = useRouter();
  const searchParams = useSearchParams();

  const USE_AUTH_HUB = useMemo(() => envFlagOn(process.env.NEXT_PUBLIC_USE_AUTH_HUB), []);
  const ENABLE_LINE = useMemo(() => envFlagOn(process.env.NEXT_PUBLIC_ENABLE_LINE), []);
  const ENABLE_GOOGLE = useMemo(() => envFlagOn(process.env.NEXT_PUBLIC_ENABLE_GOOGLE), []);
  const ENABLE_MS = useMemo(() => envFlagOn(process.env.NEXT_PUBLIC_ENABLE_MS), []);

  const HAS_SOCIAL = ENABLE_LINE || ENABLE_GOOGLE || ENABLE_MS;

  const errorParam = searchParams.get("error");
  const conflictEmail = searchParams.get("email");
  const nextParam = searchParams.get("next") || undefined;
  const showConflict = errorParam === "email_conflict";

  const handleLogin = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError("");
      setPending(true);

      try {
        const res = await fetch("/api/v1/auth/login/", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password, next: nextParam }),
          cache: "no-store",
        });

        if (!res.ok) {
          setError(
            res.status === 403
              ? "アカウントが有効化されていません"
              : "メールアドレスまたはパスワードが正しくありません"
          );
          return;
        }

        const explicitNext = searchParams.get("next");
        if (explicitNext) {
          router.push(explicitNext);
          return;
        }

        const me = await fetchCurrentUserClient();
        router.push(me && isStaffUser(me) ? "/dashboard" : "/staff");
      } catch {
        setError("通信エラーが発生しました");
      } finally {
        setPending(false);
      }
    },
    [email, password, router, searchParams, nextParam]
  );

  const startOidc = async (provider: Provider): Promise<void> => {
    setError("");
    setSocialPending(provider);

    try {
      const origin = window.location.origin;

      if (USE_AUTH_HUB) {
        const u = new URL("/auth/oidc/login", origin);
        u.searchParams.set("provider", provider);
        if (nextParam) u.searchParams.set("next", nextParam);
        window.location.href = u.toString();
        return;
      }

      const redirect_uri = `${origin}/api/v1/auth/login/${provider}/callback`;
      const res = await fetch(`/api/v1/auth/login/${provider}/start`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ redirect_uri, next: nextParam }),
      });

      const data: unknown = await res.json().catch(() => ({}));
      const redirectUrl = getStringProp(data, "redirect") ?? getStringProp(data, "url");

      if (!res.ok || !redirectUrl) {
        if (res.status === 409) {
          setError("このSNSアカウントは未登録です。サインアップから登録してください。");
        } else {
          const detail = getStringProp(data, "detail");
          setError(detail ?? "外部ログインの開始に失敗しました。");
        }
        return;
      }

      window.location.href = redirectUrl;
    } catch {
      setError("外部ログインの開始に失敗しました。");
    } finally {
      setSocialPending(null);
    }
  };

  return (
    <div
      className="
        relative flex min-h-screen items-center justify-center
        bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300
        dark:from-slate-950 dark:via-slate-900 dark:to-black
        px-4 py-12
      "
    >
      <div className="w-full max-w-md">
        <div
          className="
            overflow-hidden rounded-2xl
            bg-white/70 shadow-2xl ring-1 ring-black/10 backdrop-blur-lg
            dark:bg-slate-900/80 dark:ring-slate-700
          "
        >
          <div className="flex justify-center pt-8 pb-4">
            <Image src="/logo.png" alt="Logo" width={180} height={40} priority />
          </div>

          <div className="px-8 pb-10">
            <h1 className="mb-6 text-center text-md text-gray-700 dark:text-slate-100">
              ログイン
            </h1>

            {showConflict && (
              <p
                className="
                  mt-4 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800 ring-1 ring-yellow-200
                  dark:bg-yellow-900/40 dark:text-yellow-100 dark:ring-yellow-700/60
                "
                role="status"
                aria-live="polite"
              >
                {conflictEmail
                  ? `${conflictEmail} は既存アカウントに登録済みです。`
                  : "このメールは既存アカウントに登録済みです。"}
                一度ログインしてから、
                <span className="font-semibold">「外部連携（ログイン方法の管理）」</span>
                でログイン手段を追加してください。
              </p>
            )}

            <form onSubmit={handleLogin} className="mt-6 space-y-4" noValidate>
              <div className="relative mb-4">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400 dark:text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="メールアドレス"
                  autoComplete="email"
                  className="
                    w-full rounded-lg border border-indigo-200 bg-white/70 px-10 py-3 text-sm
                    text-gray-900 placeholder-gray-400
                    focus:outline-none focus:ring-2 focus:ring-indigo-400
                    dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-100 dark:placeholder-slate-500
                  "
                  required
                />
              </div>

              <div className="relative mb-6">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-gray-400 dark:text-slate-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="パスワード"
                  autoComplete="current-password"
                  className="
                    w-full rounded-lg border border-indigo-200 bg-white/70 px-10 py-3 text-sm
                    text-gray-900 placeholder-gray-400
                    focus:outline-none focus:ring-2 focus:ring-indigo-400
                    dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-100 dark:placeholder-slate-500
                  "
                  required
                  minLength={8}
                />
              </div>

              <button
                type="submit"
                disabled={pending}
                aria-disabled={pending}
                className="
                  w-full rounded-lg bg-gradient-to-r from-sky-800 to-sky-900
                  py-3 text-sm font-semibold text-white
                  transition hover:brightness-110 active:scale-95 disabled:opacity-60
                "
                aria-busy={pending}
              >
                {pending ? "ログイン中…" : "メールアドレス / パスワードでログイン"}
              </button>
            </form>

            <div className="mt-4 text-right text-sm text-gray-400">
              アカウントをお持ちでない方は{" "}
              <Link href={signupHref} className="underline">
                サインアップ
              </Link>
            </div>

            {HAS_SOCIAL && (
              <>
                <div className="my-6 text-center text-sm text-gray-500 dark:text-slate-400">または</div>

                <div className="space-y-2">
                  {ENABLE_LINE && (
                    <>
                      <button
                        type="button"
                        onClick={() => void startOidc("line")}
                        disabled={socialPending === "line"}
                        aria-disabled={socialPending === "line"}
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-500 py-3 text-sm font-semibold text-white transition hover:bg-green-600 active:scale-95 disabled:opacity-60"
                        aria-busy={socialPending === "line"}
                      >
                        <SiLine className="h-5 w-5" />
                        {socialPending === "line" ? "LINEにリダイレクト中…" : "LINEでログイン"}
                      </button>
                      <p className="mt-2 text-center text-xs text-gray-500 dark:text-slate-400">
                        LINEログインはアカウント連携用です。案件打診をLINEで受け取るには、
                        ログイン後に公式アカウントの友だち追加が必要です。
                      </p>
                    </>
                  )}

                  {ENABLE_GOOGLE && (
                    <button
                      type="button"
                      onClick={() => void startOidc("google")}
                      disabled={socialPending === "google"}
                      aria-disabled={socialPending === "google"}
                      className="
                        flex w-full items-center justify-center gap-2
                        rounded-lg border border-gray-200 bg-white py-3 text-sm font-semibold
                        text-gray-800 transition hover:bg-gray-50 active:scale-95 disabled:opacity-60
                        dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800
                      "
                      aria-busy={socialPending === "google"}
                    >
                      <FcGoogle className="h-5 w-5" />
                      {socialPending === "google" ? "Googleにリダイレクト中…" : "Googleでログイン"}
                    </button>
                  )}

                  {ENABLE_MS && (
                    <button
                      type="button"
                      onClick={() => void startOidc("microsoft")}
                      disabled={socialPending === "microsoft"}
                      aria-disabled={socialPending === "microsoft"}
                      className="
                        flex w-full items-center justify-center gap-2
                        rounded-lg border border-gray-200 bg-white py-3 text-sm font-semibold
                        text-gray-800 transition hover:bg-gray-50 active:scale-95 disabled:opacity-60
                        dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800
                      "
                      aria-busy={socialPending === "microsoft"}
                    >
                      <FaMicrosoft className="h-5 w-5" />
                      {socialPending === "microsoft" ? "Microsoftにリダイレクト中…" : "Microsoftでログイン"}
                    </button>
                  )}
                </div>
              </>
            )}

            {error && (
              <p
                className="mt-6 text-center text-sm text-red-600 dark:text-red-400"
                role="alert"
                aria-live="polite"
              >
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}